import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  agreementMailPages,
  agreementTexts,
  alertActions,
  buttonName,
  commonErrMsg,
  commonValidationMsg,
  identityText,
  login,
  mail,
  optimizationTexts,
  tooltips
} from "../../../locales/en";
import HeaderDashboard from "../../../components/layout/header/HeaderDashboard";
import { FaBackward, FaSpinner } from "react-icons/fa";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import "../tcGuaranteeStep.scss";
import publicAxios from "../../../utils/publicAxios";
import {
  getCheckTokenValidityUrl,
  getLeagalAgreementDownloadTokenUrl,
  getLeagalAgreementUploadTokenUrl,
  getOnboardGuaranteeFormURL,
  getAccAnnatureStatusURL
} from "../../../utils/constants/apiEndPoints";
import { showToast } from "../../../utils/utils";
import {
  MAILTO_INFO_EMAIL,
  MAX_CALL_COUNT,
  SCHEDULED_SECOND_FIVE,
  SCHEDULED_SECOND_TEN,
  showToastType
} from "../../../utils/constants/appConstants";
import { FILE_SIZE_LIMIT } from "../../../utils/constants/appConstants";
import AgreementModalId from "../../../components/modals/AgreementModalNoID";
import { NOT_FOUND } from "../../../route/paths";

function GuaranteeNoID() {
  const [showManually, setShowManually] = useState();
  const [showSubmit, setSubmit] = useState();
  const [addAgreementModal, setAddAgreementModal] = useState(false);

  const [searchParams] = useSearchParams();

  const [getToken, setToken] = useState("");
  const [data, setData] = useState("");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [anatureDataUrl, setAnatureDataUrl] = useState("");
  const fileInput = useRef(null);
  const [isExpired, setIsExpired] = useState(false);
  const [annatureStatusData, setAnnatureStatusData] = useState(null);
  const [isDisabledButton, setIsDisabledButton] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const CheckTokenValidity = useCallback(async () => {
    try {
      const response = await publicAxios.get(getCheckTokenValidityUrl, {
        params: { token: getToken }
      });

      setIsExpired(response.data.data.isUsed);
    } catch (error) {
      showToast(error.response?.data?.message, showToastType.error);
    }
  }, [getToken]);
  const navigate = useNavigate();

  // load Data ===
  const loadGuranteeData = useCallback(
    async (getToken) => {
      try {
        const response = await publicAxios.post(
          getOnboardGuaranteeFormURL,
          {},
          {
            params: {
              token: getToken
            }
          }
        );
        setData(response.data.data);
        setAnatureDataUrl(
          response?.data?.data?.signing_token_links?.[0]?.endpoint
        );
        // A document whose envelope has completed is reported as signed, so the
        // page says so instead of asking a signer to sign it again.
        setAnnatureStatusData(
          response?.data?.data?.is_signed
            ? {
                status: true,
                displayText: response?.data?.data?.signed_display_text
              }
            : null
        );
      } catch {
        navigate(NOT_FOUND);
      }
    },
    [navigate]
  );

  // The account's Annature state, read alongside the document as a second
  // opinion: it is a plain status lookup, so it still answers when re-reading
  // the document itself fails. It needs a session, and it reports the account's
  // latest envelope rather than this token's document, so it may only ever
  // confirm a signature - never withdraw one the document itself reported.
  const handleAnnatureCheckStatus = useCallback(async () => {
    const userAccountId = data?.file_data?.userAccountId;
    if (!userAccountId) {
      return;
    }
    try {
      const response = await publicAxios.post(getAccAnnatureStatusURL, null, {
        params: { userAccountId }
      });
      if (response?.data?.data?.status) {
        setAnnatureStatusData(response.data.data);
      }
    } catch {
      // A recipient may open this link without a session, and the lookup is
      // secured: leave the page as it is rather than put an error in front of them.
    }
  }, [data]);

  const handleDownload = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoading(true);
      const response = await publicAxios.get(
        getLeagalAgreementDownloadTokenUrl,
        {
          responseType: "arraybuffer",
          params: {
            token: getToken
          }
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: "application/pdf" });

      // Create a link element
      const link = document.createElement("a");

      // Set the download attribute with the filename from the response headers
      const filename = "FundTap Deed.pdf";
      link.href = URL.createObjectURL(blob);
      link.download = filename;

      // Trigger the download by simulating a click on the link
      link.click();

      // Clean up the object URL
      URL.revokeObjectURL(link.href);
    } catch {
      showToast(commonErrMsg.item11, showToastType.error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const { files } = event.target;

    // Ensure a file is selected
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];

    if (file.size > FILE_SIZE_LIMIT) {
      showToast(commonValidationMsg?.fileLimitError, showToastType.error);
      event.target.value = "";
      setFileName("");
      return;
    }

    setFileName(file.name);

    try {
      setLoadingUpload(true);

      const formDataToSend = new FormData();
      formDataToSend.append("file", file, file.name);

      const config = {
        method: "post",
        url: getLeagalAgreementUploadTokenUrl,
        headers: {
          "Content-Type": "multipart/form-data"
        },
        params: {
          token: getToken
        },
        data: formDataToSend
      };

      const response = await publicAxios(config);

      if (response.status === 200) {
        showToast(
          response.data.message ?? optimizationTexts?.fileUploaded,
          showToastType.success
        );
        setIsUploaded(true);
      }
    } catch (error) {
      if (error.response.status === 400) {
        showToast(
          agreementMailPages?.guaranteeNoID.unsupportedFile,
          showToastType.error
        );
        setFileName("");
      } else {
        showToast(error.response.data.message, showToastType.error);
        setFileName("");
      }
    } finally {
      setLoadingUpload(false);
    }
  };

  const handleUploadButtonClick = () => {
    fileInput.current.click();
  };

  // Function End

  const [successMsg, setSuccessMsg] = useState(false);

  const handleConfirm = () => {
    setSuccessMsg(true);
  };

  const toggleShowManually = () => {
    setShowManually(!showManually);
    setSubmit(true);
  };

  const handleBackToDigitalClick = () => {
    setShowManually(false);
    setSubmit(false);
  };

  // The completed state lands by webhook, so the poll that already runs when
  // the signing window closes re-reads this document - the same shape the
  // onboarding agreement step uses. A failed read leaves the page as it is;
  // the next poll, or the next visit, catches up.
  const refreshSignedState = useCallback(async () => {
    try {
      const response = await publicAxios.post(
        getOnboardGuaranteeFormURL,
        {},
        { params: { token: getToken } }
      );
      setData(response.data.data);
      if (response?.data?.data?.is_signed) {
        setAnnatureStatusData({
          status: true,
          displayText: response?.data?.data?.signed_display_text
        });
      }
    } catch {
      // leave the current state in place
    }
  }, [getToken]);

  const showAgreementModal = () => {
    if (addAgreementModal) {
      let callCount = 0;
      setIsDisabledButton(true);
      const scheduleNextCall = (delay) => {
        setTimeout(() => {
          callCount++;
          refreshSignedState();
          handleAnnatureCheckStatus();
          setIsDisabledButton(false);
          if (callCount < MAX_CALL_COUNT) {
            scheduleNextCall(SCHEDULED_SECOND_FIVE);
          }
        }, delay);
      };
      scheduleNextCall(SCHEDULED_SECOND_TEN);
    }
    setAddAgreementModal(!addAgreementModal);
  };

  // The signer is finished once the document is recorded as signed - the webhook
  // sets that flag when the envelope completes - or once they upload a signed copy.
  // The document section has nothing left to offer them either way.
  const isDocumentDone =
    Boolean(annatureStatusData?.status) ||
    Boolean(data?.file_data?.isDocumentSigned) ||
    isUploaded;

  const renderAgreementActions = () => {
    if (annatureStatusData?.status) {
      return <p className="text-success">{annatureStatusData?.displayText}</p>;
    }

    if (isDocumentDone) {
      return (
        <p className="text-success">
          {`${agreementTexts?.uploadDocsSuccessText} `}
          <a href={MAILTO_INFO_EMAIL}>{mail}</a>
        </p>
      );
    }

    return (
      <>
        <button
          type="button"
          className="btn btn-primary"
          onClick={showAgreementModal}
          disabled={isDisabledButton}
        >
          {agreementTexts?.reviewSign}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-transparent"
          onClick={toggleShowManually}
          disabled={isDisabledButton}
        >
          {agreementTexts?.signUpload}
        </button>
      </>
    );
  };

  useEffect(() => {
    const token = searchParams.get("token");
    setToken(token);
    loadGuranteeData(token);
  }, [searchParams]);

  useEffect(() => {
    if (getToken) CheckTokenValidity();
  }, [getToken]);

  return (
    <>
      <div className="steplist-wrap guaranteNoId guarantee-wrap mt-5">
        <HeaderDashboard />

        {isExpired === false ? (
          <div className="container">
            <div className="row">
              <div className="col-md-8 offset-md-2 mb-5">
                <div className="step-wrap">
                  <div className="step-title mainH1">
                    <h1>{`${login.mainTitle2} ${data?.heading_text}`}</h1>
                  </div>
                  {successMsg ? (
                    <div className="alert alert-success mt-5" role="alert">
                      {`${agreementMailPages?.addOwners.successText} `}
                      <a href={MAILTO_INFO_EMAIL}>{mail}</a>
                    </div>
                  ) : (
                    <>
                      <div className="step-content mt-3 mb-5">
                        <div
                          className={`${showManually && !isDocumentDone ? "hide" : "show"} hide-manually`}
                        >
                          {!isDocumentDone && (
                            <div className="aplyid-section">
                              <p>
                                {agreementTexts?.pleaseReview}
                                {data?.heading_text}
                                {agreementTexts?.youWill}
                              </p>
                            </div>
                          )}

                          <div className="agreement-btn-group mb-3">
                            {renderAgreementActions()}
                          </div>
                        </div>
                        {showManually && !isDocumentDone && (
                          <div className="show-manually-wrap mt-5">
                            <div className="show-manual-btn mb-5">
                              <button
                                type="button"
                                className="btn btn-primary btn-transparent"
                                onClick={handleBackToDigitalClick}
                              >
                                <FaBackward /> {agreementTexts?.reviewSign}
                              </button>
                            </div>
                            <div className="aplyid-section">
                              <p>{agreementTexts?.pleaseDownload}</p>

                              <div className="agreement-group-warp mt-5">
                                <div className="row mb-2">
                                  <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                                    <label htmlFor="Download">
                                      {agreementTexts?.downloadAgreement}
                                    </label>
                                  </div>
                                  <div className="col-md-6">
                                    <Link
                                      className={`btn btn-primary fundtap_agreement spinDiv`}
                                      id="Download"
                                      onClick={handleDownload}
                                    >
                                      {buttonName.download}
                                      {loading && (
                                        <span className="loadSpin">
                                          <FaSpinner />
                                        </span>
                                      )}
                                    </Link>
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-md-6 d-flex pt-2 pb-2 align-items-center">
                                    <label htmlFor="uploadAgreement">
                                      {agreementTexts?.uploadAgreement}
                                    </label>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="upload-file">
                                      {fileName && (
                                        <p className="uploaded-file-name">
                                          {fileName}
                                        </p>
                                      )}
                                      <button
                                        type="button"
                                        onClick={handleUploadButtonClick}
                                        className={`btn btn-primary fundtap_agreement spinDiv`}
                                      >
                                        {tooltips.upload}
                                        {loadingUpload && (
                                          <span className="loadSpin">
                                            <FaSpinner />
                                          </span>
                                        )}
                                      </button>
                                      <input
                                        name="isFile"
                                        type="file"
                                        ref={fileInput}
                                        onChange={handleFileChange}
                                        className="d-none"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {showSubmit && !isDocumentDone && (
                        <div className="next-btn p-1 agreement-btn-group">
                          <button
                            type="button"
                            className="btn btn-primary large_btn"
                            onClick={handleConfirm}
                          >
                            {alertActions.Confirm}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="row">
            <div className="col-12">
              <div className="expired-token-message text-center pt-5">
                <h3>{identityText?.tokenExpiredText}</h3>
              </div>
            </div>
          </div>
        )}
      </div>

      <AgreementModalId
        isOpen={addAgreementModal}
        toggle={showAgreementModal}
        anatureDataUrl={anatureDataUrl}
      />
    </>
  );
}
export default GuaranteeNoID;
